<template>
  <div class="text-left">
    <h5 class="text-center">
      <strong>{{ $t("name") }}:</strong> {{ title }}
      <span class="ml-5" v-if="supervisor"
        ><strong> Supervisor:</strong> {{ supervisor.username }}</span
      >
    </h5>
    <v-divider />
    <v-card>
      <form class="pa-6">
        <div class="service-sections">
          <section class="service-section">
            <div class="service-section-header">
              <h5 class="mb-0">{{ $t("form.addServices") }}</h5>
            </div>
            <div v-if="selectedAddServices.length" class="service-list">
              <div
                v-for="service in selectedAddServices"
                :key="service.code"
                class="service-row"
              >
                <div class="service-content">
                  <div class="service-label-row">
                    <span class="service-label-name">{{ service.name }}</span>
                    <v-tooltip v-if="service.restricted" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          class="ml-2"
                          color="red"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-lock
                        </v-icon>
                      </template>
                      {{ $t("form.service_restricted_tooltip") }}
                    </v-tooltip>
                    <v-tooltip v-if="service.requires_agreement" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          class="ml-2"
                          color="blue"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-file-sign
                        </v-icon>
                      </template>
                      {{ $t("form.service_requires_agreement_tooltip") }}
                    </v-tooltip>
                    <v-tooltip
                      v-if="service.exclusive_with && service.exclusive_with.length"
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          class="ml-2"
                          color="orange"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-call-split
                        </v-icon>
                      </template>
                      {{ exclusiveServicesLabel(service) }}
                    </v-tooltip>
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="service-info-button"
                      @click.stop="openServiceInfo(service)"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-services-state">
              {{ $t("form.noneMarked") }}
            </div>
          </section>

          <section class="service-section">
            <div class="service-section-header">
              <h5 class="mb-0">{{ $t("form.removeServices") }}</h5>
            </div>
            <div v-if="selectedRemoveServices.length" class="service-list">
              <div
                v-for="service in selectedRemoveServices"
                :key="service.code"
                class="service-row"
              >
                <div class="service-content">
                  <div class="service-label-row">
                    <span class="service-label-name">{{ service.name }}</span>
                    <v-tooltip v-if="service.restricted" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          class="ml-2"
                          color="red"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-lock
                        </v-icon>
                      </template>
                      {{ $t("form.service_restricted_tooltip") }}
                    </v-tooltip>
                    <v-tooltip v-if="service.requires_agreement" top>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          class="ml-2"
                          color="blue"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-file-sign
                        </v-icon>
                      </template>
                      {{ $t("form.service_requires_agreement_tooltip") }}
                    </v-tooltip>
                    <v-tooltip
                      v-if="service.exclusive_with && service.exclusive_with.length"
                      top
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          class="ml-2"
                          color="orange"
                          small
                          v-bind="attrs"
                          v-on="on"
                        >
                          mdi-call-split
                        </v-icon>
                      </template>
                      {{ exclusiveServicesLabel(service) }}
                    </v-tooltip>
                    <v-btn
                      icon
                      small
                      color="primary"
                      class="service-info-button"
                      @click.stop="openServiceInfo(service)"
                    >
                      <v-icon small>mdi-information-outline</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="empty-services-state">
              {{ $t("form.noneMarked") }}
            </div>
          </section>
        </div>
        <v-divider class="form-actions-divider" />
        <DirectorActions v-if="isAuthorize" :object_type="object_type" />
        <SupervisorActions
          v-else
          :item="investigator"
          :object_type="object_type"
          :form_name="form_name"
          :state="state"
          :supervisor="supervisor !== null ? supervisor.username : ''"
          @change-state-supervisor="onStateSupervisorChange"
        />
      </form>
    </v-card>
    <v-dialog v-model="serviceInfoDialog" max-width="600px">
      <v-card v-if="selectedService">
        <v-card-title class="headline">
          {{ selectedService.name }}
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            <strong>{{ $t("form.description") }}:</strong>
            <div
              v-if="getServiceDescription(selectedService)"
              v-html="getServiceDescription(selectedService)"
            />
            <div v-else>
              {{ $t("table.features.missingDescription") }}
            </div>
          </div>
          <div class="mb-2">
            <strong>{{ $t("form.restricted") }}:</strong>
            <span>
              {{
                selectedService.restricted
                  ? $t("table.features.restricted")
                  : $t("table.features.notRestricted")
              }}
            </span>
          </div>
          <div class="mb-2">
            <strong>{{ $t("form.requires_agreement") }}:</strong>
            <span>
              {{
                selectedService.requires_agreement
                  ? $t("table.features.requiresAgreement")
                  : $t("table.features.notRequiresAgreement")
              }}
            </span>
          </div>
          <div>
            <strong>{{ $t("form.documentation_link") }}:</strong>
            <span v-if="selectedService.documentation_link">
              <a
                :href="selectedService.documentation_link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ selectedService.documentation_link }}
              </a>
            </span>
            <span v-else>-</span>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeServiceInfo">
            {{ $t("button.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DirectorActions from "../../action_buttons/DirectorActions";
import SupervisorActions from "../../action_buttons/SupervisorActions";
import axios from "axios";
import { normalizeServices } from "../../../utils/service";
export default {
  components: { DirectorActions, SupervisorActions },
  name: "ReviewModifyServices",

  data: () => ({
    object_type: "",
    object_id: null,
    state: "",
    form_name: "",
    title: "",
    reject_comment: "",
    supervisor: "",
    services: [],
    listAddServices: [],
    listRemoveServices: [],
    serviceInfoDialog: false,
    selectedService: null,
    investigator: {
      preferred_name: {
        given_name: "",
        family_name: ""
      },
      account: {
        services: [],
        addServices: [],
        removeServices: []
      }
    }
  }),

  created() {
    this.getData();
  },

  computed: {
    isAuthorize() {
      return this.$route.meta.authorize !== undefined;
    },
    selectedAddServices() {
      return this.sortServices(this.listAddServices);
    },
    selectedRemoveServices() {
      return this.sortServices(this.listRemoveServices);
    }
  },

  methods: {
    getData() {
      let path;
      if (this.isAuthorize) {
        path =
          this.$store.state.endpoint +
          "/direction/moderatedOperations/authorize/investigator/" +
          this.$route.params.id;
      } else {
        path =
          this.$store.state.endpoint +
          "/moderatedOperations/review/investigator/" +
          this.$route.params.id;
      }
      axios
        .get(path)
        .then(response => {
          this.investigator.account = response.data.data.user.account;
          this.reject_comment = response.data.reject_comment;
          this.form_name = response.data.form_name;
          this.title = response.data.title;
          this.state = response.data.state;
          this.supervisor = response.data.supervisor;
          this.object_type = response.data.object_type;
          this.object_id = response.data.object_id;
          this.getInvestigator(this.object_id);
          this.getServices();
        })
        .catch(error => {
          console.log(error);
        });
    },

    getInvestigator(id) {
      let path = this.$store.state.endpoint + "/person/" + id;

      axios
        .get(path)
        .then(response => {
          this.investigator.preferred_name = response.data.preferred_name;
          this.object_id = response.data.id;
        })
        .catch(error => {
          console.log(error);
        });
    },
    getServices() {
      let path = this.$store.state.endpoint + "/service/no-basic";
      axios
        .get(path)
        .then(response => {
          this.services = normalizeServices(response.data);
          this.listAddServices = this.services.filter(service =>
            this.investigator.account.addServices.includes(service.id)
          );
          this.listRemoveServices = this.services.filter(service =>
            this.investigator.account.removeServices.includes(service.id)
          );
        })
        .catch(error => {
          console.log(error);
        });
    },
    sortServices(services) {
      if (!Array.isArray(services)) {
        return [];
      }
      return services.slice().sort((a, b) => {
        const orderDiff = (a.order || 0) - (b.order || 0);
        if (orderDiff !== 0) {
          return orderDiff;
        }
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
    },
    getExclusiveIds(service) {
      if (!service || !Array.isArray(service.exclusive_with)) {
        return [];
      }
      return service.exclusive_with
        .map(item => (typeof item === "object" ? item.id : item))
        .filter(item => item !== undefined && item !== null);
    },
    getExclusiveServiceName(service) {
      if (!service) {
        return "";
      }
      if (typeof service === "object") {
        if (service.name) {
          return service.name;
        }
        if (service.id !== undefined && service.id !== null) {
          const found = this.services.find(item => item && item.id === service.id);
          return found && found.name ? found.name : String(service.id);
        }
        return "";
      }
      const found = this.services.find(item => item && item.id === service);
      return found && found.name ? found.name : String(service);
    },
    exclusiveServicesLabel(service) {
      const names = this.getExclusiveIds(service)
        .map(item => this.getExclusiveServiceName(item))
        .filter(name => name);
      if (names.length === 0) {
        return this.$t("form.exclusive_with");
      }
      return this.$t("form.exclusive_with") + ": " + names.join(", ");
    },
    openServiceInfo(service) {
      this.selectedService = service;
      this.serviceInfoDialog = true;
    },
    closeServiceInfo() {
      this.serviceInfoDialog = false;
    },
    getServiceDescription(service) {
      if (!service || !service.description) {
        return "";
      }
      const locale = this.$i18n && this.$i18n.locale ? this.$i18n.locale : "es";
      if (service.description[locale]) {
        return service.description[locale];
      }
      return (
        service.description.es ||
        service.description.en ||
        service.description.gl ||
        ""
      );
    },

    onStateSupervisorChange(state, supervisor) {
      this.state = state;
      this.supervisor = supervisor;
    }
  }
};
</script>

<style scoped>
.service-sections {
  display: grid;
  gap: 1rem;
}

.service-section {
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 12px;
  padding: 1rem 1rem 0.9rem;
  background: #fcfcfd;
}

.service-section-header {
  margin-bottom: 0.9rem;
}

.service-list {
  display: grid;
  gap: 0.45rem;
}

.service-row {
  padding: 0.15rem 0;
}

.service-content {
  min-width: 0;
}

.service-label-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.service-label-name {
  line-height: 1.3;
  color: #334155;
}

.service-info-button {
  margin-left: 0.1rem;
}

.empty-services-state {
  padding: 0.9rem 1rem;
  border-radius: 10px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.95rem;
}

.form-actions-divider {
  margin-top: 1.25rem;
}

@media (min-width: 960px) {
  .service-sections {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
